import React, { useState } from 'react';
import { FolderNode } from '../../services/api';

interface FolderTreeProps {
  rootFolders: FolderNode[];
  selectedFolderId: number | null;
  onSelectFolder: (folderId: number | null) => void;
  workplaceTitle?: string;
}

const FolderTree: React.FC<FolderTreeProps> = ({
  rootFolders,
  selectedFolderId,
  onSelectFolder,
  workplaceTitle
}) => {
  const [expandedFolders, setExpandedFolders] = useState<Set<number>>(new Set());

  // Kök klasörler değiştiğinde alt klasörü olanları otomatik genişlet
  React.useEffect(() => {
    if (rootFolders && rootFolders.length > 0) {
      const idsWithChildren = new Set<number>();
      rootFolders.forEach(f => {
        if (f.children && f.children.length > 0) {
          idsWithChildren.add(f.id);
        }
      });
      setExpandedFolders(prev => new Set([...Array.from(prev), ...Array.from(idsWithChildren)]));
    }
  }, [rootFolders]);

  const toggleFolder = (folderId: number, e: React.MouseEvent) => {
    e.stopPropagation();
    const newExpanded = new Set(expandedFolders);
    if (newExpanded.has(folderId)) {
      newExpanded.delete(folderId);
    } else {
      newExpanded.add(folderId);
    }
    setExpandedFolders(newExpanded);
    onSelectFolder(folderId);
  };

  const handleSelect = (folderId: number | null, e: React.MouseEvent) => {
    e.stopPropagation();
    onSelectFolder(folderId);
    if (folderId !== null && !expandedFolders.has(folderId)) {
      const newExpanded = new Set(expandedFolders);
      newExpanded.add(folderId);
      setExpandedFolders(newExpanded);
    }
  };

  const getFolderColorAndIcon = (folderName: string, folderType: string, isExpanded: boolean) => {
    if (folderType === 'year') {
      return {
        color: '#2563eb',
        icon: '📅'
      };
    }

    if (folderType === 'month') {
      return {
        color: '#0d9488',
        icon: '🗓️'
      };
    }

    const name = folderName.toLocaleUpperCase('tr-TR');
    if (name.includes('ACİL') || name.includes('TATBİKAT')) {
      return { color: '#ef4444', icon: isExpanded ? '📂' : '📁' };
    }
    if (name.includes('EĞİTİM')) {
      return { color: '#10b981', icon: isExpanded ? '📂' : '📁' };
    }
    if (name.includes('YILLIK') || name.includes('ÇALIŞMA') || name.includes('DEĞERLENDİRME')) {
      return { color: '#f59e0b', icon: isExpanded ? '📂' : '📁' };
    }
    if (name.includes('SAĞLIK') || name.includes('EK-2') || name.includes('MUAYENE')) {
      return { color: '#ec4899', icon: isExpanded ? '📂' : '📁' };
    }
    if (name.includes('RİSK')) {
      return { color: '#8b5cf6', icon: isExpanded ? '📂' : '📁' };
    }

    return { color: '#ca8a04', icon: isExpanded ? '📂' : '📁' };
  };

  const renderTreeNodes = (nodes: FolderNode[]) => {
    return (
      <ul className="archive-tree-list">
        {nodes.map((folder) => {
          const isExpanded = expandedFolders.has(folder.id);
          const isSelected = selectedFolderId === folder.id;
          const hasChildren = folder.children && folder.children.length > 0;
          const { color, icon } = getFolderColorAndIcon(folder.name, folder.type, isExpanded);

          return (
            <li key={folder.id} className="archive-tree-item">
              <div
                className={`archive-tree-node-row ${isSelected ? 'selected' : ''}`}
                onClick={(e) => handleSelect(folder.id, e)}
              >
                {/* Genişletme / Daraltma Oku */}
                <div
                  className={`archive-tree-toggle ${isExpanded ? 'expanded' : ''}`}
                  onClick={(e) => hasChildren && toggleFolder(folder.id, e)}
                  style={{ visibility: hasChildren ? 'visible' : 'hidden' }}
                >
                  ▶
                </div>

                {/* Klasör İkonu */}
                <span className="archive-tree-icon" style={{ color }}>
                  {icon}
                </span>

                {/* Klasör Başlığı */}
                <span className="archive-tree-label" style={{ color: isSelected ? '#1e293b' : color }}>
                  {folder.name}
                </span>
              </div>

              {/* Alt Klasörler */}
              {isExpanded && hasChildren && folder.children && renderTreeNodes(folder.children)}
            </li>
          );
        })}
      </ul>
    );
  };

  return (
    <div className="archive-folder-sidebar">
      <div className="archive-folder-tree-header">
        <div
          className={`archive-tree-root-item ${selectedFolderId === null ? 'selected' : ''}`}
          onClick={(e) => handleSelect(null, e)}
        >
          <span className="archive-root-icon">🏢</span>
          <span className="archive-root-title" title={workplaceTitle || 'Kök Dizin'}>
            {workplaceTitle || 'İşyeri Arşiv Kökü'}
          </span>
        </div>
      </div>

      <div className="archive-folder-tree-body">
        {rootFolders.length === 0 ? (
          <div className="archive-tree-empty">
            <span>📁 Henüz oluşturulmuş klasör bulunmuyor.</span>
          </div>
        ) : (
          renderTreeNodes(rootFolders)
        )}
      </div>
    </div>
  );
};

export default FolderTree;
